import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutGrid, ListChecks, RefreshCw, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import KpiStrip from "@/components/tx-plan-center/KpiStrip";
import PipelineTab from "@/components/tx-plan-center/PipelineTab";
import WorkQueueTab from "@/components/tx-plan-center/WorkQueueTab";
import FilterBar from "@/components/tx-plan-center/FilterBar";
import TxPlanDrawer from "@/components/tx-plan-center/TxPlanDrawer";
import { defaultTabForRole } from "@/components/tx-plan-center/pipelineHelpers";

// =====================================================================
// Tx Plan Center — PR 1 (read-only)
// =====================================================================
// Top-level revenue pipeline command surface. Owner / Admin land on the
// kanban; Front Desk lands on the work-queue placeholder; Doctors land on
// the kanban with the "My plans only" toggle pre-applied to their doctor
// record (resolved via doctors.profile_id = auth.uid()).
//
// All filter state syncs to the URL so refresh / share / browser back
// preserves it. The list and KPI RPCs are clinic-scoped via JWT.
// =====================================================================

const TABS = [
  { key: "pipeline",   label: "Pipeline",   Icon: LayoutGrid },
  { key: "work-queue", label: "Work Queue", Icon: ListChecks },
];

const FILTER_KEYS = [
  "doctor_id", "pipeline_state", "min_value", "max_value",
  "date_from", "date_to", "patient_search", "mine",
];

const TX_PLAN_TUTORIAL_DOCTORS = [
  { id: "tutorial-doctor", display_name: "Dr. Levi Jordan", profile_id: "doctor-user", archived: false },
];

const TX_PLAN_TUTORIAL_KPIS = {
  pipeline_value_centavos: 2650000,
  active_value_centavos: 0,
  ar_value_centavos: 0,
  recovery_value_centavos: 2650000,
  acceptance_rate_pct: 0,
  acceptance_accepted_count: 0,
  acceptance_proposed_count: 1,
  lost_reasons_90d: [],
};

const TX_PLAN_TUTORIAL_PLANS = [
  {
    id: "tutorial-tx-plan-1",
    plan_code: "TP-TUTORIAL",
    patient_name: "Juan Dela Cruz",
    patient_id: "tutorial-patient",
    doctor_name: "Dr. Levi Jordan",
    doctor_id: "tutorial-doctor",
    pipeline_state: "proposed",
    plan_summary: "RCT tooth 46 followed by Zirconia Crown",
    total_centavos: 2650000,
    days_in_state: 0,
    proposed_at: "2026-05-18T10:00:00.000Z",
    accepted_at: null,
    created_at: "2026-05-18T09:30:00.000Z",
    item_count: 2,
    total_visits: 4,
    completed_visits: 0,
    open_task_count: 1,
  },
  {
    id: "tutorial-tx-plan-needs-booking",
    plan_code: "TP-ACCEPT",
    patient_name: "Ana Cruz",
    patient_id: "tutorial-patient-2",
    doctor_name: "Dr. Levi Jordan",
    doctor_id: "tutorial-doctor",
    pipeline_state: "needs_booking",
    plan_summary: "Accepted RCT plan waiting for first appointment",
    total_centavos: 2050000,
    days_in_state: 3,
    proposed_at: "2026-05-14T09:00:00.000Z",
    accepted_at: "2026-05-15T11:00:00.000Z",
    created_at: "2026-05-14T09:00:00.000Z",
    item_count: 2,
    total_visits: 4,
    completed_visits: 0,
    open_task_count: 1,
  },
];

// Pesos in the URL → centavos sent to the RPC.
function pesosToCentavos(pesoStr) {
  const n = parseInt(pesoStr, 10);
  if (Number.isNaN(n) || n < 0) return null;
  return n * 100;
}

export default function TxPlanCenter() {
  const { activeClinic, activeClinicId, clinicRole, user } = useAuth();
  const { activeBranchId } = useBranch();
  const currency = activeClinic?.currency || "PHP";

  const [searchParams, setSearchParams] = useSearchParams();
  const tutorialDemoMode = ["tx-planning", "rct-zirconia-multi-visit"].includes(searchParams.get("tutorial")) && searchParams.get("demo") === "1";

  // ----------------------- Tab (URL-synced + role default) -----------
  const tab = searchParams.get("tab") || defaultTabForRole(clinicRole);
  const setTab = useCallback((next) => {
    const sp = new URLSearchParams(searchParams);
    if (next === defaultTabForRole(clinicRole)) sp.delete("tab");
    else sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  }, [searchParams, setSearchParams, clinicRole]);

  // ----------------------- Filters (URL-synced) ----------------------
  const filters = useMemo(() => {
    const out = {};
    for (const k of FILTER_KEYS) out[k] = searchParams.get(k) || "";
    return out;
  }, [searchParams]);

  const setFilters = useCallback((next) => {
    const sp = new URLSearchParams(searchParams);
    for (const k of FILTER_KEYS) {
      if (next[k]) sp.set(k, next[k]);
      else sp.delete(k);
    }
    setSearchParams(sp, { replace: true });
  }, [searchParams, setSearchParams]);

  const resetFilters = useCallback(() => {
    const sp = new URLSearchParams();
    if (tab !== defaultTabForRole(clinicRole)) sp.set("tab", tab);
    setSearchParams(sp, { replace: true });
  }, [tab, clinicRole, setSearchParams]);

  const hasAnyFilter = FILTER_KEYS.some((k) => !!filters[k]);

  // ----------------------- Drawer (URL-synced) -----------------------
  // ?plan=<uuid> is the open state. Setting it opens the drawer; clearing
  // closes it. Refresh / share / browser-back all behave correctly.
  const drawerPlanId = searchParams.get("plan") || null;
  const openDrawer = useCallback((planId) => {
    if (!planId) return;
    const sp = new URLSearchParams(searchParams);
    sp.set("plan", planId);
    setSearchParams(sp, { replace: false });
  }, [searchParams, setSearchParams]);
  const closeDrawer = useCallback(() => {
    const sp = new URLSearchParams(searchParams);
    sp.delete("plan");
    setSearchParams(sp, { replace: false });
  }, [searchParams, setSearchParams]);

  // The drawer fires `dos:tx-plan-center-open` after a Reconfirm so the
  // newly-cloned plan opens automatically. Listen here.
  useEffect(() => {
    function onOpen(e) {
      const next = e?.detail?.planId;
      if (next) openDrawer(next);
    }
    window.addEventListener("dos:tx-plan-center-open", onOpen);
    return () => window.removeEventListener("dos:tx-plan-center-open", onOpen);
  }, [openDrawer]);

  // ----------------------- Doctor list (for filter sidebar) ----------
  const [doctors, setDoctors]       = useState([]);
  const [myDoctorId, setMyDoctorId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (tutorialDemoMode) {
      setDoctors(TX_PLAN_TUTORIAL_DOCTORS);
      setMyDoctorId(TX_PLAN_TUTORIAL_DOCTORS[0].id);
      return undefined;
    }
    if (!activeClinicId) return;
    (async () => {
      const { data, error } = await supabase
        .from("doctors")
        .select("id, display_name, profile_id, archived")
        .eq("clinic_id", activeClinicId)
        .eq("archived", false)
        .order("display_name", { ascending: true });
      if (cancelled) return;
      if (error) {
        // eslint-disable-next-line no-console
        console.warn("[tx-plan-center] doctors query failed", error);
        return;
      }
      setDoctors(data || []);
      const mine = (data || []).find((d) => d.profile_id === user?.id);
      setMyDoctorId(mine?.id || null);
    })();
    return () => { cancelled = true; };
  }, [activeClinicId, tutorialDemoMode, user?.id]);

  // For doctors, default the "mine" toggle ON unless the URL says otherwise.
  useEffect(() => {
    if (clinicRole !== "doctor") return;
    if (!myDoctorId) return;
    if (searchParams.has("mine")) return; // honor explicit URL state
    const sp = new URLSearchParams(searchParams);
    sp.set("mine", "1");
    setSearchParams(sp, { replace: true });
  }, [clinicRole, myDoctorId, searchParams, setSearchParams]);

  // ----------------------- KPI fetch ---------------------------------
  const [kpis, setKpis]               = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [kpisError, setKpisError]     = useState(null);

  const refreshKpis = useCallback(async () => {
    if (tutorialDemoMode) {
      setKpis(TX_PLAN_TUTORIAL_KPIS);
      setKpisLoading(false);
      setKpisError(null);
      return;
    }
    if (!activeClinicId) return;
    setKpisLoading(true);
    setKpisError(null);
    const { data, error } = await supabase.rpc("tx_plan_center_kpis", {
      p_branch_id: activeBranchId || null,
    });
    if (error) {
      setKpisError(error);
      setKpisLoading(false);
      return;
    }
    setKpis(data || null);
    setKpisLoading(false);
  }, [activeBranchId, activeClinicId, tutorialDemoMode]);

  // ----------------------- Plan list fetch ---------------------------
  const [plans, setPlans]             = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError]     = useState(null);

  // Build the RPC payload from the URL filters. Pesos -> centavos here.
  const rpcFilters = useMemo(() => {
    const f = {};
    if (filters.doctor_id)      f.doctor_id = filters.doctor_id;
    else if (filters.mine === "1" && myDoctorId) f.doctor_id = myDoctorId;
    if (filters.pipeline_state) f.pipeline_state = filters.pipeline_state;
    const minC = pesosToCentavos(filters.min_value);
    const maxC = pesosToCentavos(filters.max_value);
    if (minC !== null) f.min_value = minC;
    if (maxC !== null) f.max_value = maxC;
    if (filters.date_from) f.date_from = filters.date_from;
    if (filters.date_to)   f.date_to   = `${filters.date_to}T23:59:59`;
    if (filters.patient_search) f.patient_search = filters.patient_search;
    if (activeBranchId) f.branch_id = activeBranchId;
    return f;
  }, [activeBranchId, filters, myDoctorId]);

  const refreshList = useCallback(async () => {
    if (tutorialDemoMode) {
      setPlans(TX_PLAN_TUTORIAL_PLANS);
      setListLoading(false);
      setListError(null);
      return;
    }
    if (!activeClinicId) return;
    setListLoading(true);
    setListError(null);
    const { data, error } = await supabase.rpc("tx_plan_center_list", { p_filters: rpcFilters });
    if (error) {
      setListError(error);
      setListLoading(false);
      return;
    }
    setPlans(data?.plans || []);
    setListLoading(false);
  }, [activeClinicId, rpcFilters, tutorialDemoMode]);

  useEffect(() => { refreshList(); }, [refreshList]);
  useEffect(() => { refreshKpis(); }, [refreshKpis]);

  // When the user comes back to the tab after a deep-link round trip
  // (drawer → patient chart → accept plan → tab back to /tx-plans),
  // refresh both the list and the KPIs so the kanban reflects the new
  // state without a manual click. Only fires when the tab regains
  // visibility (not on every focus blur, which would be chatty).
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === "visible") {
        refreshList();
        refreshKpis();
      }
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshList, refreshKpis]);

  // ----------------------- Render ------------------------------------
  return (
    <div data-testid="tx-plan-center-page" data-tutorial-id="tx-plan-center-page" className="px-4 lg:px-6 py-5 space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold text-slate-900 tracking-tight">Tx Plan Center</h1>
          <p className="text-[13px] text-slate-500 mt-0.5">
            Every treatment plan in {activeClinic?.clinic_name || "your clinic"} — from draft to closed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {clinicRole === "doctor" && myDoctorId ? (
            <label className="inline-flex items-center gap-2 text-[12px] text-slate-700 select-none">
              <input
                type="checkbox"
                checked={filters.mine === "1"}
                onChange={(e) => setFilters({ ...filters, mine: e.target.checked ? "1" : "" })}
                className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
              />
              My plans only
            </label>
          ) : null}
          <button
            type="button"
            onClick={() => { refreshList(); refreshKpis(); }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-300 bg-white text-[12px] text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className={["w-3.5 h-3.5", listLoading || kpisLoading ? "animate-spin" : ""].join(" ")} strokeWidth={2} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI strip */}
      {kpisError ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" strokeWidth={2} />
          Couldn’t load KPIs: {String(kpisError.message || kpisError)}
        </div>
      ) : (
        <KpiStrip kpis={kpis} loading={kpisLoading} currency={currency} />
      )}

      {/* Tab switcher */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-tutorial-id="tx-plan-center-tabs">
        {TABS.map(({ key, label, Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              data-testid={`tx-plan-center-tab-${key}`}
              onClick={() => setTab(key)}
              className={[
                "inline-flex items-center gap-1.5 px-3 py-2 text-[13px] -mb-px border-b-2",
                active
                  ? "border-teal-500 text-teal-700 font-semibold"
                  : "border-transparent text-slate-500 hover:text-slate-800",
              ].join(" ")}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
              {label}
            </button>
          );
        })}
      </div>

      {/* Horizontal filter bar — Shopify-style. Reclaims horizontal
          real estate for the kanban / Work Queue below. */}
      <FilterBar
        filters={filters}
        onChange={setFilters}
        doctors={doctors}
        planCount={listLoading ? null : plans.length}
        hasAny={hasAnyFilter}
        onReset={resetFilters}
      />

      {/* Content — full width */}
      <div className="min-w-0">
        {tab === "pipeline" ? (
          <PipelineTab
            plans={plans}
            currency={currency}
            loading={listLoading}
            error={listError}
            onOpen={tutorialDemoMode ? undefined : openDrawer}
          />
        ) : (
          <WorkQueueTab
            plans={plans}
            currency={currency}
            loading={listLoading}
            error={listError}
            onOpen={tutorialDemoMode ? undefined : openDrawer}
          />
        )}
      </div>

      <TxPlanDrawer
        planId={drawerPlanId}
        currency={currency}
        onClose={closeDrawer}
        onPlanChanged={() => { refreshList(); refreshKpis(); }}
      />
    </div>
  );
}
